import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import * as styles from "./rules-editor.module.scss";
import {
	VelavuAPI,
	VelavuRule,
	VelavuRuleValidationError,
	VelavuRuleValidationErrorType,
} from "velavu-js-api";
import { VelavuModalProvider } from "../../../elements/velavu-modal";
import RulesEditorSidebar from "./rules-editor-sidebar";
import useRulesEditorState from "./state/rules-editor-state";
import { RulesEditorToolbarPropsSaveState } from "./rules-editor-toolbar";
import { useNavigate } from "react-router";
import RulesEditorCard from "./rules-editor-card";
import { makeStyleable } from "../../../helper/icon-helper";
import IconAction from "../../../dynamicicons/icon-action";
import RulesEditorCardIcon from "./rules-editor-card-icon";
import RulesEditorConnector from "./rules-editor-connector";
import IconStar from "../../../dynamicicons/icon-star";
import RulesEditorTriggerSection from "./rules-editor-trigger-section";
import {
	NormalizedRule,
	normalizedRuleToVelavuRule,
	velavuRuleToNormalizedRule,
} from "./state/normalized/normalized-rule";
import { deepCompare } from "../../../helper/object-helper";
import RulesEditorAlertSection from "./rules-editor-alert-section";
import RulesEditorDeviceConfigSection from "./rules-editor-device-config-section";
import { rulesEditorErrorStateHasError } from "./state/input/rules-editor-error-state";
import { useVelavuToast } from "../../../elements/velavu-toast";
import { ToastNotificationNoInternet } from "../../../elements/toast-notification-builtins";
import ToastNotification from "../../../elements/toast-notification";
import { normalizedRuleActionIsEmpty } from "./state/normalized/normalized-rule-action";
import whitelabel from "../../../whitelabel";
import { useHasPermission } from "../../sign-in-context/sign-in-context";
import { doesRuleContainConfig } from "../rules-permission-helper";
import { getPermissionDeniedMessage } from "../../../helper/permission-helper";

export interface RulesEditorProps {
	rule: VelavuRule | null;
}

export default function RulesEditor(props: RulesEditorProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const canWriteRules = useHasPermission("rules:write");
	const canWriteRulesWithConfig = useHasPermission("rules:write-config");

	const isRuleWritable = useMemo(() => {
		//No rules are writable if we don't have the basic write permission
		if (!canWriteRules) {
			return false;
		}

		//Make sure we have a rule
		if (props.rule === null) {
			return true;
		}

		//Only allow editing rules with a config if we have special permission
		return !doesRuleContainConfig(props.rule) || canWriteRulesWithConfig;
	}, [props.rule, canWriteRules, canWriteRulesWithConfig]);

	const { pushToast } = useVelavuToast();

	const state = useRulesEditorState(props.rule);

	const [savedRule, setSavedRule] = useState<NormalizedRule | null>(() => {
		if (props.rule === null) {
			return null;
		} else {
			return velavuRuleToNormalizedRule(props.rule);
		}
	});
	const [ruleID, setRuleID] = useState(props.rule?.id);

	const editorMatchesSavedRule = useMemo(() => {
		return deepCompare(state.computedRule, savedRule, {
			collapseDefaultValues: false,
		});
	}, [state.computedRule, savedRule]);
	const [hasSaved, setHasSaved] = useState(false);
	const canSave = useMemo(() => {
		if (!isRuleWritable) {
			return false;
		} else if (rulesEditorErrorStateHasError(state.error)) {
			return false;
		} else if (savedRule !== null && editorMatchesSavedRule) {
			//If the rule is identical to the saved rule, disable saving
			return false;
		} else {
			//Make sure the rule has the necessary fields
			return (
				state.computedRule.condition !== "" &&
				!normalizedRuleActionIsEmpty(state.computedRule.action)
			);
		}
	}, [
		isRuleWritable,
		state.error,
		editorMatchesSavedRule,
		state.computedRule,
		savedRule,
	]);
	const [isSaving, setIsSaving] = useState(false);
	const save = useCallback(async () => {
		if (!canSave) {
			return;
		}

		setIsSaving(true);
		const ruleContents = normalizedRuleToVelavuRule(state.computedRule);
		let updatedRule: VelavuRule;
		try {
			if (ruleID === undefined) {
				//Make a new rule
				updatedRule = await VelavuAPI.rules.createNewRule(ruleContents);
			} else {
				//Update the existing rule
				updatedRule = await VelavuAPI.rules.modifyExistingRule(
					ruleID,
					ruleContents,
				);
			}
		} catch (error) {
			console.warn("Failed to save rule:", error);

			//Push an error toast
			if (error instanceof VelavuRuleValidationError) {
				let message: string;
				switch (error.reason) {
					case VelavuRuleValidationErrorType.InvalidCondition:
						message = t("rules.editor.errors.invalid_condition");
						break;
					case VelavuRuleValidationErrorType.InvalidAction:
						message = t("rules.editor.errors.invalid_action");
						break;
					case VelavuRuleValidationErrorType.InvalidPriority:
						message = t("rules.editor.errors.invalid_priority");
						break;
					default:
						message = t("rules.editor.errors.invalid_details");
						break;
				}

				pushToast((close) => (
					<ToastNotification
						type="error"
						title={t("rules.editor.save_failed_title")}
						onDismiss={close}
					>
						{message}
					</ToastNotification>
				));
			} else {
				pushToast((close) => (
					<ToastNotificationNoInternet onDismiss={close} />
				));
			}

			return;
		} finally {
			setIsSaving(false);
		}

		//Update the URL to include the new rule ID and state
		navigate(`/rules/${updatedRule.id}`, {
			state: { rule: updatedRule },
			replace: true,
		});

		//Update the saved rule state
		setSavedRule(velavuRuleToNormalizedRule(updatedRule));
		setRuleID(updatedRule.id);
		setHasSaved(true);
	}, [
		canSave,
		state.computedRule,
		ruleID,
		setIsSaving,
		setSavedRule,
		setRuleID,
		setHasSaved,
		pushToast,
		navigate,
		t,
	]);
	const saveState = useMemo((): RulesEditorToolbarPropsSaveState => {
		if (isSaving) {
			return "saving";
		} else if (canSave) {
			return "enabled";
		} else if (hasSaved && editorMatchesSavedRule) {
			return "saved";
		} else {
			return "disabled";
		}
	}, [isSaving, canSave, hasSaved, editorMatchesSavedRule]);

	const goBack = useCallback(() => {
		navigate("/rules");
	}, [navigate]);

	const permissionDeniedTooltip = !isRuleWritable
		? getPermissionDeniedMessage(
				canWriteRules ? "rules:write-config" : "rules:write",
				t,
			)
		: undefined;

	return (
		<VelavuModalProvider>
			<div className={styles.container}>
				<RulesEditorSidebar
					className={styles.sidebar}
					onBack={goBack}
					saveState={saveState}
					onSave={save}
					saveTooltip={permissionDeniedTooltip}
					ruleName={state.input.name}
					onChangeRuleName={state.input.setName}
					ruleDescription={state.input.description}
					onChangeRuleDescription={state.input.setDescription}
					ruleEnabled={state.input.enabled}
					onChangeRuleEnabled={state.input.setEnabled}
					disabled={!isRuleWritable}
				/>

				<div className={styles.content}>
					<div className={styles.editor}>
						<RulesEditorCard
							icon={
								<RulesEditorCardIcon
									icon={makeStyleable(IconStar)}
									backgroundColor={
										whitelabel.palette.equipmentLight
									}
									foregroundColor={
										whitelabel.palette.equipmentDark
									}
								/>
							}
							title={t("rules.editor.trigger")}
						>
							<RulesEditorTriggerSection
								condition={state.input.condition}
								onChangeCondition={state.input.setCondition}
								priority={state.input.priority}
								onChangePriority={state.input.setPriority}
							/>
						</RulesEditorCard>

						<RulesEditorConnector />

						<RulesEditorCard
							icon={
								<RulesEditorCardIcon
									icon={makeStyleable(IconAction)}
									backgroundColor="#FFE5F8"
									foregroundColor="#FF00B7"
								/>
							}
							title={t("rules.editor.action")}
						>
							<RulesEditorAlertSection
								title={t("rules.editor.activation_alert")}
								settings={state.input.activationEvent}
								onChangeSettings={
									state.input.setActivationEvent
								}
								error={state.error.activationEvent}
							/>

							<RulesEditorAlertSection
								title={t("rules.editor.deactivation_alert")}
								settings={state.input.deactivationEvent}
								onChangeSettings={
									state.input.setDeactivationEvent
								}
								error={state.error.deactivationEvent}
							/>

							<RulesEditorDeviceConfigSection
								config={state.input.deviceConfig}
								onChangeConfig={state.input.setDeviceConfig}
								error={state.error.deviceConfig}
								disabled={!canWriteRulesWithConfig}
								disabledTooltip={
									!canWriteRulesWithConfig
										? getPermissionDeniedMessage(
												"rules:write-config",
												t,
											)
										: undefined
								}
							/>
						</RulesEditorCard>
					</div>
				</div>
			</div>
		</VelavuModalProvider>
	);
}
